import { useEffect, useState } from 'react'
import { Alert, Col, Container, Form, Row } from 'react-bootstrap'
import KLayerNavigation from '../components/KLayerNavigation'
import KButton from '../components/KButton'
import { isUserLoggedIn, showKAlert } from '../helpers'
import { t } from '../locale'
import {
  deleteTransparenzChConnection,
  getTransparenzChConnectionStatus,
  saveTransparenzChConnection,
  TransparenzChConnectionStatus,
} from '../service/transparenzCh'

const emptyForm = { clientId: '', clientSecret: '', refreshToken: '' }

export default function SwissTransparenzregisterSettings() {
  const [status, setStatus] = useState<TransparenzChConnectionStatus | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    void fetchStatus()
  }, [])

  const fetchStatus = async () => {
    try {
      setStatus(await getTransparenzChConnectionStatus())
    } catch {
      setStatus(null)
    }
  }

  const save = async () => {
    if (!form.clientId.trim() || !form.clientSecret.trim() || !form.refreshToken.trim()) {
      showKAlert('danger', t('transparenzChAllFieldsRequired'))
      return
    }

    setSaving(true)
    try {
      const res = await saveTransparenzChConnection({
        clientId: form.clientId.trim(),
        clientSecret: form.clientSecret.trim(),
        refreshToken: form.refreshToken.trim(),
      })
      if (res.ok) {
        setForm(emptyForm)
        showKAlert('success', 'transparenzChConnectionSaved')
        await fetchStatus()
      }
    } finally {
      setSaving(false)
    }
  }

  const disconnect = async () => {
    const res = await deleteTransparenzChConnection()
    if (res.ok) {
      setForm(emptyForm)
      setStatus({ configured: false })
    }
  }

  return (
    <Container>
      <Row>
        <div className='sidebar col-md-3 col-lg-2 p-0'>
          <KLayerNavigation logged={isUserLoggedIn()} />
        </div>
        <main className='ms-sm-auto col-lg-10 px-md-4 md-4'>
          <div className='col-12'>
            <p className='pe-none text-draft lead fw-bold link-offset-3 link-underline-primary'>
              {t('transparenzChSettingsTitle')}
            </p>
          </div>

          <div className='col-12 my-5'>
            <Row>
              <Col lg={4}>
                <div className='d-flex justify-between section-title-blade'>
                  <div className='pe-4 sm:px-0'>
                    <p className='mt-1 text-draft'>{t('transparenzChSettingsHint')}</p>
                  </div>
                </div>
              </Col>
              <Col lg={8}>
                <div className='px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md'>
                  {status?.configured && (
                    <Alert
                      variant={status.needsReauth ? 'danger' : 'success'}
                      className='d-flex justify-content-between align-items-center'
                    >
                      <span>
                        <i
                          className={`bi ${
                            status.needsReauth
                              ? 'bi-exclamation-triangle-fill'
                              : 'bi-check-circle-fill'
                          } me-2`}
                        ></i>
                        {status.needsReauth
                          ? t('transparenzChNeedsReauth')
                          : t('transparenzChConnected')}
                        {status.clientId ? ` — ${status.clientId}` : ''}
                      </span>
                      <KButton
                        id='btn-transparenz-ch-disconnect'
                        label={t('transparenzChDisconnect')}
                        variant='outline-danger'
                        icon='x-circle'
                        size='sm'
                        onClick={disconnect}
                      />
                    </Alert>
                  )}
                  {status && !status.configured && (
                    <p className='text-muted'>{t('transparenzChNotConnected')}</p>
                  )}

                  <Form>
                    <Form.Group className='mb-3'>
                      <Form.Label className='fw-medium smaller text-primary'>
                        {t('transparenzChClientId')}
                      </Form.Label>
                      <Form.Control
                        type='text'
                        value={form.clientId}
                        onChange={e => setForm({ ...form, clientId: e.target.value })}
                      />
                    </Form.Group>
                    <Form.Group className='mb-3'>
                      <Form.Label className='fw-medium smaller text-primary'>
                        {t('transparenzChClientSecret')}
                      </Form.Label>
                      <Form.Control
                        type='password'
                        autoComplete='new-password'
                        value={form.clientSecret}
                        onChange={e => setForm({ ...form, clientSecret: e.target.value })}
                      />
                    </Form.Group>
                    <Form.Group className='mb-3'>
                      <Form.Label className='fw-medium smaller text-primary'>
                        {t('transparenzChRefreshToken')}
                      </Form.Label>
                      <Form.Control
                        as='textarea'
                        rows={3}
                        value={form.refreshToken}
                        onChange={e => setForm({ ...form, refreshToken: e.target.value })}
                      />
                    </Form.Group>
                    <KButton
                      id='btn-transparenz-ch-save'
                      label={t('transparenzChSave')}
                      variant='primary'
                      icon='plug'
                      disabled={saving}
                      onClick={save}
                    />
                  </Form>
                </div>
              </Col>
            </Row>
          </div>
        </main>
      </Row>
    </Container>
  )
}
